import {SettingParam, SettingParamTypes} from "@/types/node";
import {useEffect, useState, useCallback, memo} from "react";
import {Button} from "../ui/Button";
import {Plus, Trash} from "lucide-react";
import {Popover, PopoverContent, PopoverTrigger} from "../ui/Popover";
import {SettingInput} from "./SettingInput";
import {SettingTextarea} from "./SettingTextarea";
import {SettingSelect} from "./SettingSelect";
import {Checkbox} from "../ui/Checkbox";
import {Icon} from "../ui/icon";
import {CaptureVariableIcon} from "../icons/CaptureVariableIcon";
type CaptureVariable = {
  name: string;
  type: string;
  instructions: string;
  required: boolean;
};

const CaptureVariableTypeOptions = [
  {name: "String", value: "string"},
  {name: "Number", value: "number"},
  {name: "Boolean", value: "boolean"},
];

const PopoverContentContainer = memo(
  ({
    value,
    selectedNodeId,
    disabled = false,
    setOpen,
    isEditing,
    handleVariableUpdate,
    handleVariableAdd,
  }: {
    value: CaptureVariable;
    selectedNodeId: string;
    disabled: boolean;
    setOpen: (open: boolean) => void;
    isEditing: boolean;
    handleVariableUpdate: (value: CaptureVariable) => void;
    handleVariableAdd: (value: CaptureVariable) => void;
  }) => {
    const [variable, setVariable] = useState<CaptureVariable>(value);

    useEffect(() => {
      setVariable(value);
    }, [value, selectedNodeId]);

    return (
      <PopoverContent side="left" sideOffset={8} className="w-98">
        <div className="flex flex-col">
          <div className="flex flex-row gap-4">
            <div className="flex-grow">
              <SettingInput
                param={{
                  label: "Name",
                  type: SettingParamTypes.INPUT,
                  placeholder: "variable_name",
                  key: "name",
                  value: variable?.name,
                }}
                handleChange={(_param, value) =>
                  setVariable({...variable, name: value})
                }
                selectedNodeId={selectedNodeId}
                disabled={disabled}
              />
            </div>
            <div className="">
              <SettingSelect
                param={{
                  label: "Type",
                  type: SettingParamTypes.SELECT,
                  key: "type",
                  value: variable?.type,
                }}
                handleChange={(_param, value) => {
                  setVariable({...variable, type: value});
                  setOpen(true);
                }}
                placeholder="Select Type"
                disabled={disabled}
                selectOptions={CaptureVariableTypeOptions}
                selectedNodeId={selectedNodeId}
              />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <SettingTextarea
              param={{
                label: "How to Collect Variable",
                type: SettingParamTypes.TEXTAREA,
                placeholder:
                  "Enter instructions on how to collect the variable",
                key: "instructions",
                value: variable?.instructions,
              }}
              handleChange={(_param, value) =>
                setVariable({...variable, instructions: value})
              }
              selectedNodeId={selectedNodeId}
              disabled={disabled}
            />
          </div>
          <div className="flex flex-row gap-2 items-center">
            <Checkbox
              checked={variable?.required}
              onCheckedChange={value =>
                setVariable({...variable, required: !!value})
              }
              id="required"
            />
            <label
              htmlFor="required"
              className="text-sm font-medium text-label"
            >
              Required
            </label>
          </div>
          <div className="flex gap-2 mt-4">
            <Button
              className="w-full"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              className="w-full"
              onClick={() => {
                if (isEditing) {
                  handleVariableUpdate(variable);
                } else {
                  handleVariableAdd(variable);
                }
              }}
              disabled={
                !variable?.name || !variable?.type || !variable?.instructions
              }
            >
              {isEditing ? "Update" : "Save"}
            </Button>
          </div>
        </div>
      </PopoverContent>
    );
  },
);

const VariableItem = memo(
  ({
    variable,
    index,
    totalCount,
    currentIndex,
    open,
    setOpen,
    setPopoverTopPosition,
    onVariableClick,
    onVariableDelete,
    disabled,
    selectedNodeId,
    currentValue,
    handleVariableUpdate,
    handleVariableAdd,
    isEditing,
  }: {
    variable: CaptureVariable;
    index: number;
    totalCount: number;
    currentIndex: number | undefined;
    open: boolean;
    setOpen: (open: boolean) => void;
    setPopoverTopPosition: (pos: number) => void;
    onVariableClick: (
      variable: CaptureVariable | null,
      index: number | undefined,
    ) => void;
    onVariableDelete: (variable: CaptureVariable) => void;
    disabled: boolean;
    selectedNodeId: string;
    currentValue: CaptureVariable;
    handleVariableUpdate: (value: CaptureVariable) => void;
    handleVariableAdd: (value: CaptureVariable) => void;
    isEditing: boolean;
  }) => {
    return (
      <Popover
        key={index}
        open={open && currentIndex === totalCount - index - 1}
        setOpen={isOpen => {
          setOpen(isOpen);
          if (!isOpen) onVariableClick(null, undefined);
        }}
      >
        <PopoverTrigger className="w-full">
          <div
            ref={ref => {
              if (ref) setPopoverTopPosition(ref.offsetTop);
            }}
            onClick={() => {
              const reversedIndex = totalCount - index - 1;
              onVariableClick(variable, reversedIndex);
            }}
            className="border rounded-lg p-2.5 flex flex-row justify-between items-center gap-2.5 cursor-pointer"
          >
            <div>
              <Icon className="w-11 h-11 bg-slate">
                <CaptureVariableIcon className="w-6 h-6 text-secondary" />
              </Icon>
            </div>
            <div className="flex flex-col overflow-hidden whitespace-nowrap items-start">
              <p className="text-sm text-primary font-medium overflow-hidden whitespace-nowrap text-ellipsis">
                {variable.name}
              </p>
              <div className="flex flex-row gap-1 text-secondary font-normal text-sm overflow-hidden whitespace-nowrap text-ellipsis">
                {variable.type && <div>{variable.type}</div>}
                {variable.required && (
                  <div className="relative pl-3 before:content-['•'] before:absolute before:left-0 before:text-secondary before:text-lg before:font-bold before:!leading-5">
                    Required
                  </div>
                )}
                {variable.instructions && (
                  <div className="relative pl-3 before:content-['•'] before:absolute before:left-0 before:text-secondary before:text-lg before:font-bold before:!leading-5 overflow-hidden whitespace-nowrap text-ellipsis">
                    {variable.instructions}
                  </div>
                )}
              </div>
            </div>
            <Button
              variant="ghost"
              size="icon-sm"
              className="[&_svg]:size-5 ml-auto"
              onClick={e => {
                e.stopPropagation();
                onVariableDelete(variable);
              }}
            >
              <Trash className="text-secondary" />
            </Button>
          </div>
        </PopoverTrigger>
        <PopoverContentContainer
          value={currentValue}
          selectedNodeId={selectedNodeId}
          disabled={disabled}
          setOpen={setOpen}
          isEditing={isEditing}
          handleVariableUpdate={handleVariableUpdate}
          handleVariableAdd={handleVariableAdd}
        />
      </Popover>
    );
  },
);

export const SettingCapture = ({
  param,
  handleChange,
  selectedNodeId,
  disabled = false,
}: {
  param: SettingParam & {value: CaptureVariable[]};
  handleChange: (param: SettingParam, value: CaptureVariable[]) => void;
  selectedNodeId: string;
  disabled: boolean;
}) => {
  const [open, setOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState<CaptureVariable>();
  const [currentIndex, setCurrentIndex] = useState<number>();
  const [values, setValues] = useState<CaptureVariable[]>(param.value || []);
  const [popoverTopPosition, setPopoverTopPosition] = useState<number>(0);

  useEffect(() => {
    setValues(param.value || []);
  }, [param, selectedNodeId]);

  const handleVariableAdd = useCallback(
    (value: CaptureVariable) => {
      setValues(prev => {
        const newValues = [...prev, value];
        handleChange(param, newValues);
        return newValues;
      });
      setOpen(false);
      setCurrentIndex(undefined);
    },
    [param, handleChange],
  );

  const handleVariableUpdate = useCallback(
    (value: CaptureVariable) => {
      setValues(prev => {
        const newValues = prev.map((v, i) => (i === currentIndex ? value : v));
        handleChange(param, newValues);
        return newValues;
      });
      setOpen(false);
      setCurrentIndex(undefined);
    },
    [currentIndex, param, handleChange],
  );

  const handleVariableDelete = useCallback(
    (value: CaptureVariable) => {
      setValues(prev => {
        const newValues = prev.filter(v => v.name !== value.name);
        handleChange(param, newValues);
        return newValues;
      });
      setOpen(false);
      setCurrentIndex(undefined);
    },
    [param, handleChange],
  );

  const handleVariableClick = useCallback(
    (variable: CaptureVariable | null, index: number | undefined) => {
      if (variable) {
        setCurrentValue({...variable});
        setCurrentIndex(index);
        setIsEditing(true);
        setOpen(true);
      } else {
        setCurrentIndex(undefined);
      }
    },
    [],
  );

  return (
    <div>
      <Popover
        open={open && currentIndex === undefined}
        setOpen={isOpen => {
          setOpen(isOpen);
          if (!isOpen) setCurrentIndex(undefined);
        }}
      >
        <PopoverTrigger className="w-full">
          <Button
            variant="outline"
            className="w-full px-3 gap-1.5"
            asDiv
            onClick={() => {
              setIsEditing(false);
              setCurrentValue({
                name: "",
                type: "",
                instructions: "",
                required: false,
              });
              setCurrentIndex(undefined);
            }}
          >
            <Plus className="text-primary-accent w-4 h-4" />
            Add Variable
          </Button>
        </PopoverTrigger>
        <PopoverContentContainer
          value={currentValue}
          selectedNodeId={selectedNodeId}
          disabled={disabled}
          setOpen={setOpen}
          isEditing={isEditing}
          handleVariableUpdate={handleVariableUpdate}
          handleVariableAdd={handleVariableAdd}
        />
      </Popover>
      <div className="flex flex-col gap-3 mt-4">
        {[...values].reverse().map((variable, index) => (
          <VariableItem
            key={index}
            variable={variable}
            index={index}
            totalCount={values.length}
            currentIndex={currentIndex}
            open={open}
            setOpen={setOpen}
            setPopoverTopPosition={setPopoverTopPosition}
            onVariableClick={handleVariableClick}
            onVariableDelete={handleVariableDelete}
            disabled={disabled}
            selectedNodeId={selectedNodeId}
            currentValue={currentValue}
            handleVariableUpdate={handleVariableUpdate}
            handleVariableAdd={handleVariableAdd}
            isEditing={isEditing}
          />
        ))}
      </div>
    </div>
  );
};
